import store from '@/store/index';
import Cookies from 'js-cookie';
import { DateTime } from 'luxon';

export const isMobile = (route) => (to, from, next) => {
  store.getters.getMobile ? next() : next(`/${route}/home`);
};

/**
 * 로그인 여부를 확인하는 함수
 * @returns {Promise<boolean>}
 */
export const getLoginState = async () => {
  if (store.getters.isLogin) return true;

  try {
    await store.dispatch('getLoginState');

    return true;
  } catch (error) {
    return false;
  }
};

function handleLoggedOutRedirect(from, next) {
  window.bvModal.show('login-required');
  // 최초 진입 시 로그인 페이지로 이동
  return next(from.name === null ? '/login' : false);
}

/**
 * 전화번호 인증 여부를 확인하는 함수
 * @param {*} updateData
 */
export const isPhoneAuth =
  (updateData = []) =>
  async (to, from, next) => {
    store.commit('sidebar', { mode: 'close' });

    // store.getters.isPhoners
    for (const data of updateData) {
      await store.dispatch(data);
    }

    const isLogin = await getLoginState();

    // 로그인이 되어있지 않다면 로그인 모달 노출 및 로그인 페이지로 이동
    if (!isLogin) {
      return handleLoggedOutRedirect(from, next);
    }

    // 전화번호 인증이 되어있지 않다면 전화번호 인증 모달 노출
    if (!store.getters.isPhone) {
      store.commit('setPhoneAuthModalState', { shown: true });
      return next(false);
    }

    return next();
  };

export const requireAuth = () => async (to, from, next) => {
  store.commit('sidebar', { mode: 'close' });

  const isLogin = await getLoginState();

  // 로그인이 되어있지 않다면 로그인 모달 노출 및 로그인 페이지로 이동
  if (!isLogin) {
    return handleLoggedOutRedirect(from, next);
  }

  return next();
};

/**
 * 유저 정보를 미리 가져오는 함수
 * @returns {Promise<void>}
 */
export const requireAuthWithPrefetchedUserInfo = () => async (to, from, next) => {
  store.commit('sidebar', { mode: 'close' });

  const isLogin = await getLoginState();

  // 로그인이 되어있지 않다면 로그인 모달 노출 및 로그인 페이지로 이동
  if (!isLogin) {
    return handleLoggedOutRedirect(from, next);
  }

  // 사용자 정보를 미리 가져옴
  await store.dispatch('getUserData');

  return next();
};

export const checkAuth = () => (to, from, next) => {
  store.commit('sidebar', { mode: 'close' });

  if (!store.getters.isLogin) store.dispatch('getLoginState');
  return next();
};

export const isChangeableNickname = () => {
  return new Promise((resolve) => {
    window.axios
      .get(`${process.env.VUE_APP_TEST_SERVER}/user/changeNickName`)
      .then((response) => {
        const resData = response.data.data;
        resolve(resData);
      })
      .catch((err) => {
        if (err.response.status === 401) {
          window.bvModal.show('login-required');
        }
      });
    // .finally(() => {
    //   resolve([2002, 2219]);
    // });
  });
};

export const beforeEnterForm = () => (to, from, next) => {
  if (store.getters.isLogin) {
    if (store.getters.isPhone) {
      if (store.getters.niceAuth || store.state.lang === 'en') {
        isChangeableNickname().then((bool) => {
          if (bool) {
            window.bvModal.show('change-nickName-modal');
            store.commit('setNewCommunityState', { community: to.name });
          } else {
            return to.name === 'EditCommunity' || to.name === 'EditInfluencer'
              ? isEmptyObj(to.params)
                ? next({ name: 'Community' })
                : next()
              : next();
          }
        });
      } else {
        window.bvModal.show('nice-id-check');
      }
    } else {
      store.commit('setPhoneAuthModalState', { shown: true });
    }
  } else {
    window.bvModal.show('login-required');
  }
};

export const isEmptyObj = (obj) => obj.constructor === Object && Object.keys(obj).length === 0;

export const getUrlParams = () => {
  let params = {};

  window.location.search.replace(/[?&]+([^=&]+)=([^&]*)/gi, (str, key, value) => {
    params[key] = value;
  });

  return params;
};

export const getUrlPathname = () => {
  const { pathname } = opener.location;
  return pathname.split('/')[1];
};

export const setNiceIdDoneContents = () => {
  switch (getUrlPathname()) {
    case 'community':
      return {
        content: '본인인증이 성공적으로 완료되었습니다.<br />닉네임 변경 후 커뮤니티 등록 신청을 진행해주세요.',
        cancelBtn: true,
      };

    default:
      return;
  }
};

export const checkNiceId = (bool) => (to, from, next) => {
  if (opener) {
    if (bool) {
      //! 성공시 팝업
      const contents = setNiceIdDoneContents();

      opener.store.dispatch('getLoginState');
      opener.store.commit('setNiceIdDone', {
        success: true,
        content: contents.content,
        cancelBtn: contents.cancelBtn,
      });
    } else {
      //! 실패시 팝업
      if (!isEmptyObj(getUrlParams())) {
        const content = decodeURI(getUrlParams().msg);

        opener.store.commit('setNiceIdDone', {
          success: false,
          content: content.replaceAll('|', '<br />'),
          cancelBtn: false,
        });
      }
    }

    opener.bvModal.show('nice-id-done');
    window.close();
  } else {
    next({ path: '/main', replace: true });
  }
};

export const isHoliday = () => {
  return new Promise((resolve) => {
    const isHolidayInStore = !!store.getters.holiday;
    const holidayInStorage = JSON.parse(localStorage.getItem('holiday'));
    const isHolidayInStorage = !!holidayInStorage?.data;
    if (isHolidayInStore) return resolve(true);
    else if (isHolidayInStorage) {
      store.commit('setHoliday', holidayInStorage);
      resolve(true);
    } else {
      store.dispatch('getHolidayName');
      resolve(false);
    }
  });
};

export const isInThePeriod = () => async (to, from, next) => {
  const isHoliday_ = await isHoliday();

  if (!isHoliday_) return next({ name: 'Main', replace: true });
  if (!store.getters.holidayDate) return next({ name: 'Main', replace: true });
  const { start, end } = store.getters.holidayDate;
  const setMidnight = (date) => {
    const [dateWithoutTime] = date?.split('T');
    return DateTime.fromISO(dateWithoutTime);
  };
  const d1 = start,
    d2 = end,
    fn = from.name;
  const dateNow = DateTime.now().toISO();
  const nowToMidnight = setMidnight(dateNow);
  const date1 = setMidnight(d1);
  const date2 = setMidnight(d2);

  if (store.getters.isLogin) {
    return nowToMidnight >= date1 && nowToMidnight <= date2
      ? next()
      : from.name !== 'Main'
      ? next({ name: 'Main', replace: true })
      : null;
  } else {
    store
      .dispatch('getLoginState')
      .then(() => next())
      .catch(() => {
        if (fn !== null) return window.bvModal.show('login-required');
        return next();
      });
  }
};

export const isNoticeCookieAvailable = () => {
  const cookie = Cookies.get('notice');

  console.log('notice cookie:', cookie);

  // 쿠키가 없으면 공지 보여주기
  return cookie === undefined;
};

/**
 * @description 문자열이 Base64 인코딩 되어있는지 확인하는 함수
 * @param {string} str - 확인할 문자열
 * @returns {boolean} - Base64 인코딩 되어있는지 여부
 */
export function isBase64Encoded(str) {
  // 빈 문자열이거나 문자열이 아닌 경우 체크
  if (!str || typeof str !== 'string') return false;

  // Base64 허용 문자만 포함하는지 검사
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(str)) return false;

  // 길이는 4의 배수여야 함
  if (str.length % 4 !== 0) return false;

  // 패딩 문자가 있는 경우 검사
  if (str.includes('=')) {
    const paddingIndex = str.indexOf('=');
    // 패딩은 끝에만 올 수 있으며 최대 2개까지 가능
    if (paddingIndex < str.length - 2) {
      return false;
    }
    // 패딩 이후에 다른 문자가 올 수 없음
    if (str.slice(paddingIndex).replace(/=/g, '').length > 0) {
      return false;
    }
  }

  return true;
}

/**
 * @description Base64 디코딩 함수
 * @param {string} str - 디코딩할 문자열
 * @returns {string} - 디코딩된 문자열
 * @throws {Error} - 디코딩 실패 시 에러 발생
 */
export function base64Decode(str) {
  try {
    // Base64 디코딩
    const binaryString = window.atob(str);
    // 바이너리 문자열을 Uint8Array로 변환
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    // UTF-8로 디코딩
    return new TextDecoder().decode(bytes);
  } catch {
    throw new Error('디코딩 실패');
  }
}

/**
 * @description 리다이렉트 함수
 * @returns {void}
 */
export function handleRedirect() {
  const redirectUri = localStorage.getItem('redirectUri');
  localStorage.removeItem('redirectUri');

  if (redirectUri === null) return;

  const decodedRedirectUri = base64Decode(redirectUri);

  window.location.href = decodedRedirectUri;
}
